import { reactive, ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import type { SearchDeal } from '@/components/DealsSearch/DealCard.vue'

export type SortKey = 'best' | 'newest' | 'discount' | 'price' | 'grade'

export interface SearchFilters {
    q: string
    sort: SortKey
    grade: string[]
    audience: number[]
    // Hierarchical drill-down: a single category id (or null for "all").
    // When set, deals are scoped to its subtree AND the sidebar shows the
    // selected category's children with rolled-up counts.
    category: number | null
    brand: string[]
    retailer: string[]
    discount: number[]
    flags: string[]
    priceMin: number | null
    priceMax: number | null
    pctOffMin: number | null
    pctOffMax: number | null
    inStock: boolean
    hasPromo: boolean
    showNotADeal: boolean
}

export interface SearchFacets {
    grade: { tier: string; count: number }[]
    audience: { bucket: number; count: number }[]
    brand: { name: string; count: number }[]
    retailer: { name: string; count: number }[]
    discount: { code: number; name: string; count: number }[]
    flags: { amazonsChoice: number; bestSeller: number; prime: number; soldByAmazon: number }
    category: { id: number; name: string; count: number }[]
    // Root-to-leaf chain of the currently-selected category — drives the
    // breadcrumb display in the sidebar. Empty when no category is selected.
    categoryAncestors: { id: number; name: string }[]
    priceBuckets: { range: string; count: number }[]
}

const emptyFacets = (): SearchFacets => ({
    grade: [], audience: [], brand: [], retailer: [], discount: [], category: [],
    categoryAncestors: [], priceBuckets: [],
    flags: { amazonsChoice: 0, bestSeller: 0, prime: 0, soldByAmazon: 0 },
})

const defaultFilters = (): SearchFilters => ({
    q: '', sort: 'best',
    grade: [], audience: [], category: null, brand: [], retailer: [], discount: [], flags: [],
    priceMin: null, priceMax: null, pctOffMin: null, pctOffMax: null,
    inStock: false, hasPromo: false, showNotADeal: false,
})

const PAGE_SIZE = 24

// Map between filter state and URL querystring. Same keys as the API
// (`grade`, `audience`, `priceMin`, etc.) so the URL doubles as a shareable
// API call.
const filtersToQuery = (f: SearchFilters): Record<string, string> => {
    const q: Record<string, string> = {}
    if (f.q) q.q = f.q
    if (f.sort && f.sort !== 'best') q.sort = f.sort
    if (f.grade.length) q.grade = f.grade.join(',')
    if (f.audience.length) q.audience = f.audience.join(',')
    if (f.category != null) q.category = String(f.category)
    if (f.brand.length) q.brand = f.brand.join(',')
    if (f.retailer.length) q.retailer = f.retailer.join(',')
    if (f.discount.length) q.discount = f.discount.join(',')
    if (f.flags.length) q.flags = f.flags.join(',')
    if (f.priceMin != null) q.priceMin = String(f.priceMin)
    if (f.priceMax != null) q.priceMax = String(f.priceMax)
    if (f.pctOffMin != null) q.pctOffMin = String(f.pctOffMin)
    if (f.pctOffMax != null) q.pctOffMax = String(f.pctOffMax)
    if (f.inStock) q.inStock = '1'
    if (f.hasPromo) q.hasPromo = '1'
    if (f.showNotADeal) q.showNotADeal = '1'
    return q
}

const queryToFilters = (query: Record<string, any>): Partial<SearchFilters> => {
    const get = (k: string) => typeof query[k] === 'string' ? query[k] as string : undefined
    const splitStr = (s: string | undefined) => s ? s.split(',').filter(Boolean) : []
    const splitNum = (s: string | undefined) => splitStr(s).map(Number).filter(n => !isNaN(n))
    const num = (s: string | undefined) => {
        if (!s) return null
        const n = Number(s); return isNaN(n) ? null : n
    }
    return {
        q: get('q') ?? '',
        sort: (get('sort') as SortKey) || 'best',
        grade: splitStr(get('grade')),
        audience: splitNum(get('audience')),
        category: num(get('category')),
        brand: splitStr(get('brand')),
        retailer: splitStr(get('retailer')),
        discount: splitNum(get('discount')),
        flags: splitStr(get('flags')),
        priceMin: num(get('priceMin')),
        priceMax: num(get('priceMax')),
        pctOffMin: num(get('pctOffMin')),
        pctOffMax: num(get('pctOffMax')),
        inStock: get('inStock') === '1',
        hasPromo: get('hasPromo') === '1',
        showNotADeal: get('showNotADeal') === '1',
    }
}

// Optional source-of-audience-score selector. Default = label-blend
// (the AudienceScore column populated by label_model + clicky blend).
// 'pair' switches /deals/search to rank by PairAudienceScore (the
// LambdaRank pair-model output) so /deals-pairs and /deals can sit
// side-by-side reading different score columns from the same data.
export type AudienceSource = 'label' | 'pair'

export function useDealsSearch(audienceSource: AudienceSource = 'label') {
    const route = useRoute()
    const router = useRouter()

    // Hydrate filter state from the URL on first construction so deep links
    // (e.g. /deals?grade=Great&inStock=1) start with the right state.
    const initial = { ...defaultFilters(), ...queryToFilters(route.query as any) }
    const filters = reactive<SearchFilters>(initial)
    const deals = ref<SearchDeal[]>([])
    const total = ref(0)
    const facets = ref<SearchFacets>(emptyFacets())
    const loading = ref(false)
    const error = ref<string | null>(null)
    const page = ref(1)

    let debounce: number | undefined

    // Build the params object for the API. Empty/default values are omitted
    // by the api helper so we don't send `grade=` for empty arrays.
    const buildParams = () => ({
        q: filters.q || undefined,
        sort: filters.sort,
        showNotADeal: filters.showNotADeal || undefined,
        grade: filters.grade.length ? filters.grade.join(',') : undefined,
        audience: filters.audience.length ? filters.audience.join(',') : undefined,
        category: filters.category != null ? filters.category : undefined,
        brand: filters.brand.length ? filters.brand.join(',') : undefined,
        retailer: filters.retailer.length ? filters.retailer.join(',') : undefined,
        discount: filters.discount.length ? filters.discount.join(',') : undefined,
        flags: filters.flags.length ? filters.flags.join(',') : undefined,
        priceMin: filters.priceMin ?? undefined,
        priceMax: filters.priceMax ?? undefined,
        pctOffMin: filters.pctOffMin ?? undefined,
        pctOffMax: filters.pctOffMax ?? undefined,
        inStock: filters.inStock || undefined,
        hasPromo: filters.hasPromo || undefined,
        // Only emit when explicitly 'pair' so /deals continues to send
        // a clean param set (server treats absence as 'label').
        audienceSource: audienceSource === 'pair' ? 'pair' : undefined,
        page: page.value,
        pageSize: PAGE_SIZE,
    })

    const runSearch = async (append: boolean = false) => {
        loading.value = true
        error.value = null
        try {
            const resp = await api.searchDeals(buildParams())
            const newDeals = resp.data?.deals ?? []
            deals.value = append ? [...deals.value, ...newDeals] : newDeals
            total.value = resp.data?.total ?? 0
            facets.value = resp.data?.facets ?? emptyFacets()
        } catch (e: any) {
            error.value = e?.response?.data?.error || e?.message || 'Failed to load deals'
        } finally {
            loading.value = false
        }
    }

    const resetAndSearch = () => {
        page.value = 1
        deals.value = []
        runSearch(false)
    }

    const loadMore = () => {
        page.value += 1
        runSearch(true)
    }

    // Toggle a value in a multi-select filter array.
    const toggleMulti = <K extends 'grade' | 'audience' | 'brand' | 'retailer' | 'discount' | 'flags'>(
        dim: K,
        value: SearchFilters[K] extends (infer U)[] ? U : never,
    ) => {
        const arr = filters[dim] as any[]
        const idx = arr.indexOf(value)
        if (idx >= 0) arr.splice(idx, 1)
        else arr.push(value)
    }

    // Drill into / out of a category. Sets the single selected category id;
    // pass null to go back to "all categories" (root level).
    const setCategory = (id: number | null) => {
        filters.category = id
    }

    const resetFilters = () => {
        Object.assign(filters, defaultFilters())
    }

    const hasActiveFilters = computed(() => {
        const f = filters
        return f.q !== '' || f.grade.length > 0 || f.audience.length > 0
            || f.category != null || f.brand.length > 0 || f.retailer.length > 0
            || f.discount.length > 0 || f.flags.length > 0
            || f.priceMin !== null || f.priceMax !== null
            || f.pctOffMin !== null || f.pctOffMax !== null
            || f.inStock || f.hasPromo || f.showNotADeal
    })

    // Debounced auto-search whenever filters change. 250ms is short enough
    // to feel responsive on toggles, long enough to avoid firing per keystroke.
    // Same handler also writes filters back into the URL so deep links work
    // and the browser's back button steps through filter states.
    watch(filters, () => {
        if (debounce) window.clearTimeout(debounce)
        debounce = window.setTimeout(() => {
            resetAndSearch()
            router.replace({ query: filtersToQuery(filters) }).catch(() => { /* ignore nav-duplicated */ })
        }, 250)
    }, { deep: true })

    return {
        filters,
        deals,
        total,
        facets,
        loading,
        error,
        page,
        hasActiveFilters,
        runSearch,
        resetAndSearch,
        loadMore,
        toggleMulti,
        setCategory,
        resetFilters,
    }
}
